import { format, formatDistanceToNow, parseISO } from "date-fns";

export function formatDateTime(dateString: string): string {
  return format(parseISO(dateString), "MMM dd, yyyy HH:mm");
}

export function formatDate(dateString: string): string {
  return format(parseISO(dateString), "MMM dd, yyyy");
}

export function formatTime(dateString: string): string {
  return format(parseISO(dateString), "HH:mm:ss");
}

export function formatTimeAgo(dateString: string): string {
  return formatDistanceToNow(parseISO(dateString), { addSuffix: true });
}

export function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${Math.round(minutes)}m`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = Math.round(minutes % 60);

  if (hours < 24) {
    return `${hours}h ${remainingMinutes}m`;
  }

  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;

  return `${days}d ${remainingHours}h ${remainingMinutes}m`;
}

export function formatDetailedDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  return `${hours.toString().padStart(2, "0")}:${mins
    .toString()
    .padStart(2, "0")}`;
}

export function formatSpeed(speed: number | undefined): string {
  if (speed === undefined) return "N/A";
  return `${speed.toFixed(1)} km/h`;
}

export function formatBattery(batteryLevel: number | undefined): string {
  if (batteryLevel === undefined) return "N/A";
  return `${batteryLevel}%`;
}

export function formatDistance(distanceKm: number): string {
  if (distanceKm < 1) {
    return `${Math.round(distanceKm * 1000)}m`;
  }
  return `${distanceKm.toFixed(2)}km`;
}

export function formatCoordinate(coord: number): string {
  return coord.toFixed(6);
}

export function getBatteryColor(batteryLevel: number | undefined): string {
  if (batteryLevel === undefined) return "text-gray-400";
  if (batteryLevel > 50) return "text-green-500";
  if (batteryLevel > 20) return "text-yellow-500";
  return "text-red-500";
}

export function getStatusColor(status: string): string {
  switch (status) {
    case "active":
      return "text-green-500";
    case "inactive":
      return "text-gray-500";
    case "maintenance":
      return "text-yellow-500";
    default:
      return "text-gray-400";
  }
}

export function getStatusBadgeColor(status: string): string {
  switch (status) {
    case "active":
      return "bg-green-100 text-green-800";
    case "inactive":
      return "bg-gray-100 text-gray-800";
    case "maintenance":
      return "bg-yellow-100 text-yellow-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B";

  const units = ["B", "KB", "MB", "GB", "TB"];
  const k = 1024;
  const decimals = 1;

  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const size = bytes / Math.pow(k, i);

  return `${size.toFixed(decimals)} ${units[i]}`;
}
